import SelectorFilter from "./selector-filter";

export default class RevealContent {

	private elements = null;
	private openClass = 'c-reveal--open';
	private moreText = 'Show more';
	private lessText = 'Show less';
	private closedMaxHeight = 300;
	private syncGroups = new Map<string, HTMLElement[]>();

	constructor(elements?: NodeListOf<Element>) {

		if (elements) {
			this.elements = elements;
		} else {
			this.elements = document.querySelectorAll('.js-reveal-content');
		}

		if (!this.elements.length) return;

		this.setupRevealSections();
		this.setupSynchronizedGroups();

	}

	private setupRevealSections() {

		for (var i = 0; i < this.elements.length; i++) {

			if (this.elements[i].classList.contains('c-reveal--done')) {
				continue;
			}

			if (this.elements[i].getAttribute('data-reveal-mobile-only') && this.elements[i].getAttribute('data-reveal-mobile-only') === 'True' && this.isDesktopLayout()) {
				this.elements[i].classList.remove('c-reveal--active');
				this.elements[i].classList.remove('js-reveal-content');
				continue;
			}

			const name = 'reveal-' + i;

			const section = this.elements[i] as HTMLElement;
			const closedMaxHeightAttribute = section.getAttribute('data-reveal-closed-max-height');
			if (closedMaxHeightAttribute) {
				this.closedMaxHeight = parseInt(closedMaxHeightAttribute);
			}

			if ((section.classList.contains('c-reveal--active-larger') || section.classList.contains('c-reveal--active-product-viewer')) && (section.clientHeight - 40) < this.closedMaxHeight) {
				section.classList.remove('c-reveal');
				section.classList.remove('c-reveal--active');
				section.classList.remove('c-reveal--active-larger');
				section.classList.remove('c-reveal--active-product-viewer');
				continue;
			}

			if (closedMaxHeightAttribute) {
				section.style.maxHeight = this.closedMaxHeight + 'px';
			}

			section.setAttribute('id', name);

			let button = this.createButton(name);

			section.appendChild(button);

			this.wrapSection(section);

			this.toggleChildrenFocusability(section, false);

			button.addEventListener('click', (e) => {
				this.handleRevealToggle(e, section, button, closedMaxHeightAttribute);
			});

			section.classList.add('c-reveal--done');

		}

	}

	private handleRevealToggle(e: Event, section: HTMLElement, button: HTMLButtonElement, closedMaxHeightAttribute: string | null) {
		const target = e.target as HTMLButtonElement;
		const parent = target.parentNode as HTMLElement;
		const isCurrentlyOpen = section.classList.contains(this.openClass);
		const syncGroup = this.getSyncGroupForElement(section);
		
		if (syncGroup && syncGroup.length > 1) {
			this.toggleSyncGroup(syncGroup, !isCurrentlyOpen);
		} else {
			this.toggleSingleSection(section, button, parent, closedMaxHeightAttribute, !isCurrentlyOpen);
		}
	}

	private toggleSingleSection(section: HTMLElement, button: HTMLButtonElement, parent: HTMLElement, closedMaxHeightAttribute: string | null, shouldOpen: boolean) {
		
		if (shouldOpen) {
			section.classList.add(this.openClass);
			section.style.maxHeight = parent.offsetHeight + 'px';
			button.setAttribute('aria-expanded', 'true');
			button.innerHTML = this.lessText;
			const timeout = setTimeout(() => { 
				section.style.maxHeight = '3000px'; 
				this.toggleChildrenFocusability(section, true); 
			}, 350);
		} else {
			section.classList.remove(this.openClass);
			section.setAttribute('style', '');
			button.setAttribute('aria-expanded', 'false');
			button.innerHTML = this.moreText;

			if (closedMaxHeightAttribute) {
				section.style.maxHeight = parseInt(closedMaxHeightAttribute) + 'px';
			}

			setTimeout(() => { this.toggleChildrenFocusability(section, false); }, 350);
		}
	}

	private toggleSyncGroup(syncGroup: HTMLElement[], shouldOpen: boolean) {
		syncGroup.forEach(section => {
			const button = section.querySelector('.js-reveal-content-button') as HTMLButtonElement;
			const parent = button?.parentNode as HTMLElement;
			const closedMaxHeightAttribute = section.getAttribute('data-reveal-closed-max-height');
			
			if (button && parent) {
				this.toggleSingleSection(section, button, parent, closedMaxHeightAttribute, shouldOpen);
			}
		});
	}

	private setupSynchronizedGroups() {
		const rowGroups = new Map<HTMLTableRowElement, HTMLElement[]>();

		this.elements.forEach((element: HTMLElement) => {
			if (!element.classList.contains('c-reveal--done')) return;

			// Auto-group by table row (any table row with multiple reveals)
			const tableRow = element.closest('tr') as HTMLTableRowElement;
			if (tableRow) {
				if (!rowGroups.has(tableRow)) {
					rowGroups.set(tableRow, []);
				}
				rowGroups.get(tableRow)!.push(element);
			}
		});

		let groupIndex = 0;
		rowGroups.forEach((sections, row) => {
			if (sections.length > 1) {
				this.syncGroups.set(`row-${groupIndex++}`, sections);
			}
		});
	}

	private getSyncGroupForElement(element: HTMLElement): HTMLElement[] | null {
		for (const sections of this.syncGroups.values()) {
			if (sections.includes(element)) {
				return sections;
			}
		}
		return null;
	}

	private toggleChildrenFocusability(container: HTMLElement, allowFocus: boolean) {

		const focusableElements = SelectorFilter.excludeByClass(container.querySelectorAll('a, button, input, textarea, select, details'), 'js-reveal-content-button');
		
		for (let i = 0, len = focusableElements.length; i < len; i++) {
			const currentElement = focusableElements[i];
			if (allowFocus) {
				currentElement.removeAttribute("tabIndex");
			} else {
				if (!this.elementIsVisible(currentElement)) {
					currentElement.tabIndex = -1;
				} else {
					currentElement.removeAttribute("tabIndex");
				}
			}
		}
	}

	private elementIsVisible(element: HTMLElement) {

		const elementTop = element.getBoundingClientRect().top;
		const parentElement = element.closest('.js-reveal-content');
		const parentTop = parentElement.getBoundingClientRect().top;
		const positionTopInContainer = elementTop - parentTop;
		const revealFadeHeight = 40;

		if (positionTopInContainer + revealFadeHeight > parseInt(window.getComputedStyle(parentElement).maxHeight.replace('px', ''))) {
			return false;
		}

		return true;

	}

	private wrapSection(section: HTMLElement) {

		const wrapper = document.createElement('div');
		wrapper.setAttribute('class', 'c-reveal__inner');
		while (section.firstChild) { wrapper.appendChild(section.firstChild); }
		section.appendChild(wrapper);

	}

	private createButton(name: string) {

		const button = document.createElement('button');
		button.setAttribute('type', 'button');
		button.setAttribute('aria-controls', name);
		button.setAttribute('aria-expanded', 'false');
		button.setAttribute('data-test', 'reveal-button');
		button.classList.add('c-reveal__button');
		button.classList.add('js-reveal-content-button');
		button.innerHTML = this.moreText;

		return button;

	}

	private isDesktopLayout() {
		return matchMedia('(min-width: 980px)').matches;
	}

}